import { useMemo } from 'react';
import moment from 'moment';
import { Grid, TextField, Tooltip, Box, Typography, InputAdornment } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {  fieldDateFormat } from '../../../constants';
import LessMoreTextFieldComp from '../../../shared_elements/components/LessMoreTextFieldComp';
import { numberWithCommas, roundAmount } from '../../../utils';
import { hasLlpEvent } from '../utils';

const ClaimField = ({ claim, shopVisit, llpClaimTotal }) => {
  const isLlpEvent = hasLlpEvent(shopVisit);
  const llpClaimAmount = llpClaimTotal;
  const claimPeriodEndDate = shopVisit?.claim_approval_period && moment(shopVisit?.shop_visit_end_date).add(shopVisit?.claim_approval_period, "day").isAfter(shopVisit?.shop_visit_end_date) ? moment(shopVisit?.shop_visit_end_date).add(shopVisit?.claim_approval_period, "day") : null;
  const isAfterAllowedDate = claim?.submission_date && (
    (claimPeriodEndDate && moment(claim.submission_date).isAfter(moment(claimPeriodEndDate))) ||
    (!claimPeriodEndDate && shopVisit?.shop_visit_end_date && moment(claim.submission_date).isAfter(moment(shopVisit.shop_visit_end_date)))
  );

  // Engine (asset_type === 2) module claim amounts, derived straight from claim_events.
  const nonLlpModules = useMemo(() => {
    if (!Array.isArray(claim?.claim_events)) return [];
    const modules = [];
    claim.claim_events.forEach((event) => {
      if (event?.maintenance_program?.is_llp_event === false) {
        (event?.modules || []).forEach((mod) => {
          modules.push({ ...mod, shop_visit_event_id: event?.shop_visit_event_id });
        });
      }
    });
    return modules;
  }, [claim?.claim_events]);

  const nonLlpEventsWithoutModules = useMemo(() => {
    if (!Array.isArray(claim?.claim_events)) return [];
    return claim.claim_events.filter((event) => event?.maintenance_program?.is_llp_event === false && (!event?.modules || event.modules.length === 0));
  }, [claim?.claim_events]);
  return (
    <Grid container spacing={2}>
      <Grid size={{ md: 4, xs: 12 }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="Submission Date"
            value={claim?.submission_date ? moment(claim.submission_date) : null}
            slotProps={{
              textField: {
                InputLabelProps: { shrink: true },
                size: "small",
                margin: "normal",
              },
            }}
            sx={{ width: "100%" }}
            format={fieldDateFormat}
            disabled={true}
          />
        </LocalizationProvider>
      </Grid>
      {shopVisit?.asset_type !== 2 ? <Grid size={{ md: 4, xs: 12 }}>
        <TextField
          id="claim_amount"
          value={claim?.claim_amount !== '' && claim?.claim_amount !== null && claim?.claim_amount !== undefined ? numberWithCommas(roundAmount(claim?.claim_amount)) : claim?.claim_amount}
          label="Claim Amount"
          variant="outlined"
          size="small"
          margin="normal"
          slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
          fullWidth
          disabled={true}
        />
      </Grid> : null}

      <Grid size={{ md: 4, xs: 12 }}>
        <TextField
          id="lessor_contribution"
          value={claim?.lessor_contribution !== '' && claim?.lessor_contribution !== null && claim?.lessor_contribution !== undefined ? numberWithCommas(roundAmount(claim?.lessor_contribution)) : claim?.lessor_contribution}
          label="Lessor Contribution"
          variant="outlined"
          size="small"
          margin="normal"
          slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
          fullWidth
          disabled={true}
        />
      </Grid>

      {isLlpEvent ?
        <Grid size={{ md: 4, xs: 12 }}>
          <TextField
            id="llp_claim_amount"
            label="LLP Claim Amount"
            fullWidth
            size="small"
            margin="normal"
            value={llpClaimAmount !== '' && llpClaimAmount !== null && llpClaimAmount !== undefined ? numberWithCommas(roundAmount(llpClaimAmount)) : llpClaimAmount}
            slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
            disabled={true}
          />
        </Grid> : null}

      {shopVisit?.asset_type === 2 ?
        <>
          {nonLlpModules?.length > 0 ? (
            <Grid size={{ md: 12, xs: 12 }} >
              <Typography className='section-header-text'><u>Engine PRSV Claim Cost Split Details:</u></Typography>
              <Box className="escalation-container">
                <Box className="escalation-box">
                  <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 1.5, columnGap: 3 }}>
                    {nonLlpModules.map((module) => {
                      const fieldKey = `${module.shop_visit_event_id}_${module.shop_visit_event_module_id}`;
                      const amount = module?.claim_amount;
                      return (
                        <TextField
                          key={fieldKey}
                          id={`${fieldKey} Claim Amount`}
                          label={
                            module?.is_non_llp ? (
                              <span style={{ verticalAlign: 'super' }}>
                                {module?.module_name}
                                <Tooltip placement='right' title={<span>Non-LLP</span>} arrow>
                                  <InfoIcon fontSize='small' color='primary' style={{ verticalAlign: 'middle', marginLeft: '2px', fontSize: 'medium' }} />
                                </Tooltip>
                              </span>
                            ) : module?.module_name
                          }
                          margin="normal"
                          value={amount !== '' && amount !== null && amount !== undefined ? numberWithCommas(roundAmount(amount)) : amount}
                          slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                          disabled={true}
                          size="small"
                          sx={{ width: 150, flexShrink: 0 }}
                        />
                      )
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
          ) : null}
          {nonLlpEventsWithoutModules?.length > 0
            ? nonLlpEventsWithoutModules.map((event) => (
              <Grid size={{ md: 4, xs: 12 }} key={event.shop_visit_event_id}>
                <TextField
                  id={`event_${event.shop_visit_event_id}_claim_amount`}
                  value={event?.claim_amount !== '' && event?.claim_amount !== null && event?.claim_amount !== undefined ? numberWithCommas(roundAmount(event?.claim_amount)) : event?.claim_amount}
                  label={event?.maintenance_program?.name || "PRSV Claim Amount"}
                  variant="outlined"
                  size="small"
                  margin="normal"
                  slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                  fullWidth
                  disabled={true}
                />
              </Grid>
            ))
            : null}
        </> : null}
      {isAfterAllowedDate ? (
        <Grid size={{ md: 12, xs: 12 }}>
          <LessMoreTextFieldComp label="Period Extension Reason" value={claim?.period_extend_reason || ""} />
        </Grid>
      ) : null}
      <Grid size={{ md: 12, xs: 12 }}>
        <LessMoreTextFieldComp label="Claim Details" value={claim?.description || ""} />
      </Grid>
      <Grid size={{ md: 12, xs: 12 }}>
        <LessMoreTextFieldComp label="Remarks" value={claim?.remarks || ""} />
      </Grid>
    </Grid>
  )
}

export default ClaimField